import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Search, Building2, Upload, Users } from "lucide-react";
import { toast } from "sonner";
import BulkImportDialog from "@/components/BulkImportDialog";
import ContactImportDialog from "@/components/ContactImportDialog";

const STATUSES = ["prospect", "active", "at_risk", "churned"];
const HEALTH = ["green", "amber", "red"];

const StatusBadge = ({ status }) => {
  const map = {
    prospect: "bg-slate-100 text-slate-700 border-slate-200",
    active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    at_risk: "bg-amber-50 text-amber-700 border-amber-200",
    churned: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded text-[11px] font-medium uppercase tracking-wider border ${map[status] || map.prospect}`}
    >
      {status?.replace("_", " ")}
    </span>
  );
};

const HealthDot = ({ h }) => {
  const c = h === "red" ? "bg-red-500" : h === "amber" ? "bg-amber-500" : "bg-emerald-500";
  return <span className={`inline-block h-2 w-2 rounded-full ${c}`} />;
};

export default function Accounts() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [liveOnly, setLiveOnly] = useState(false);
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [contactImportOpen, setContactImportOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    name: "",
    industry: "",
    website: "",
    status: "prospect",
    health: "green",
    num_users: 0,
    arr: 0,
    mrr: 0,
    notes: "",
  });

  const load = async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (status !== "all") params.status = status;
    if (ownerFilter !== "all") params.owner_name = ownerFilter;
    const r = await api.get("/accounts", { params });
    setAccounts(r.data);
    setLoading(false);
  };

  const [allOwners, setAllOwners] = useState([]);
  useEffect(() => {
    (async () => {
      // Fetch all accounts once (no filters) to populate the owner filter dropdown
      try {
        const r = await api.get("/accounts");
        const owners = Array.from(new Set(r.data.map(a => a.owner_name).filter(Boolean))).sort();
        setAllOwners(owners);
      } catch (_) { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [status, ownerFilter]);

  const onCreate = async () => {
    if (!form.name.trim()) {
      toast.error("Account name is required");
      return;
    }
    try {
      const r = await api.post("/accounts", {
        ...form,
        num_users: Number(form.num_users) || 0,
        arr: Number(form.arr) || 0,
        mrr: Number(form.mrr) || 0,
      });
      toast.success(`Created ${r.data.name}`);
      setOpen(false);
      setForm({
        name: "",
        industry: "",
        website: "",
        status: "prospect",
        health: "green",
        num_users: 0,
        arr: 0,
        mrr: 0,
        notes: "",
      });
      navigate(`/app/accounts/${r.data.id}`);
    } catch (e) {
      toast.error("Failed to create account");
    }
  };

  return (
    <div className="space-y-6" data-testid="accounts-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-700">
            Customer Records
          </div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Accounts</h1>
          <p className="text-slate-500 text-sm mt-1">
            One unified record per customer — deals, contracts, billing, support and Stax usage.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            data-testid="import-contacts-btn"
            onClick={() => setContactImportOpen(true)}
            className="gap-2"
          >
            <Users className="h-4 w-4" /> Import contacts
          </Button>
          <Button
            variant="outline"
            data-testid="bulk-import-btn"
            onClick={() => setBulkOpen(true)}
            className="gap-2"
          >
            <Upload className="h-4 w-4" /> Bulk import
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-account-btn" className="gap-2 bg-blue-600 hover:bg-blue-700">
                <Plus className="h-4 w-4" /> New Account
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create new account</DialogTitle>
              <DialogDescription>
                The master record for a Xentra customer. You can add contacts, deals and Stax later.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Account name *</Label>
                <Input
                  data-testid="account-form-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Leeds United Football Club"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Industry</Label>
                  <Input
                    value={form.industry}
                    onChange={(e) => setForm({ ...form, industry: e.target.value })}
                    placeholder="Sports / Education / Retail"
                  />
                </div>
                <div>
                  <Label>Website</Label>
                  <Input
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                    placeholder="example.co.uk"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Status</Label>
                  <Select
                    value={form.status}
                    onValueChange={(v) => setForm({ ...form, status: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Health</Label>
                  <Select
                    value={form.health}
                    onValueChange={(v) => setForm({ ...form, health: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {HEALTH.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>ARR (£)</Label>
                  <Input
                    type="number"
                    value={form.arr}
                    onChange={(e) => setForm({ ...form, arr: e.target.value })}
                  />
                </div>
                <div>
                  <Label>MRR (£)</Label>
                  <Input
                    type="number"
                    value={form.mrr}
                    onChange={(e) => setForm({ ...form, mrr: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="account-form-submit" onClick={onCreate} className="bg-blue-600 hover:bg-blue-700">
                Create Account
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <BulkImportDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        onApplied={load}
      />

      <ContactImportDialog
        open={contactImportOpen}
        onClose={() => setContactImportOpen(false)}
        onImported={load}
      />

      <Card className="border-slate-200">
        <CardContent className="p-4 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="accounts-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search accounts by name…"
              className="pl-9"
            />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-44" data-testid="accounts-filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={ownerFilter} onValueChange={setOwnerFilter}>
            <SelectTrigger className="w-48" data-testid="accounts-filter-owner">
              <SelectValue placeholder="Owner" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All owners</SelectItem>
              {allOwners.map((o) => (
                <SelectItem key={o} value={o} data-testid={`accounts-owner-option-${o}`}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load}>Search</Button>
          <Button
            variant={liveOnly ? "default" : "outline"}
            onClick={() => setLiveOnly(!liveOnly)}
            data-testid="live-only-toggle"
            className="ml-auto"
          >
            {liveOnly ? "Live only ✓" : "Live only"}
          </Button>
        </CardContent>
      </Card>

      <Card className="border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500 font-mono text-sm">Loading…</div>
        ) : accounts.length === 0 ? (
          <div className="p-12 text-center">
            <Building2 className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <div className="font-heading text-lg text-slate-700">No accounts yet</div>
            <div className="text-sm text-slate-500 mt-1">
              Click <span className="font-medium text-slate-700">New Account</span> to create your first customer record.
            </div>
          </div>
        ) : (
          <table className="w-full" data-testid="accounts-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                <th className="px-4 py-3 w-8"></th>
                <th className="px-4 py-3">Account</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Users</th>
                <th className="px-4 py-3">ARR</th>
                <th className="px-4 py-3">MRR</th>
                <th className="px-4 py-3">Owner</th>
                <th className="px-4 py-3">Created</th>
              </tr>
            </thead>
            <tbody>
              {accounts.filter(a => !liveOnly || a.is_live).map((a) => (
                <tr
                  key={a.id}
                  data-testid={`account-row-${a.id}`}
                  onClick={() => navigate(`/app/accounts/${a.id}`)}
                  className="border-b border-slate-100 hover:bg-blue-50/40 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3"><HealthDot h={a.health} /></td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900 flex items-center gap-2">
                      {a.name}
                      {a.is_live && (
                        <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200" title={a.live_through ? `Live through ${new Date(a.live_through).toLocaleDateString('en-GB')}` : 'Live customer'}>
                          Live
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">
                      {a.industry || "—"}
                      {a.website && <> · {a.website}</>}
                    </div>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
                  <td className="px-4 py-3 font-mono text-sm text-slate-700 text-right">{a.num_users || 0}</td>
                  <td className="px-4 py-3 font-mono text-sm text-slate-900">{fmtMoney(a.arr)}</td>
                  <td className="px-4 py-3 font-mono text-sm text-slate-700">{fmtMoney(a.mrr)}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{a.owner_name || "—"}</td>
                  <td className="px-4 py-3 text-sm text-slate-500">{fmtDate(a.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
